// MovementChart — the shared odds-movement widget primitive (Phase 3). Used by
// Match Goals/Cards - Movement and Winner (1x2) - Movement (and later the Player
// movements).
//
// React owns the whole body: the stat header (OPEN / NOW / MOVE) + label are
// declarative; the D3 line chart is drawn imperatively in a useEffect. The legacy
// rebuilds the SVG from scratch on every draw (no animation), so this does too —
// no persistent skeleton, no enter/update/exit. A ResizeObserver redraws on
// resize; both are disconnected on cleanup. Only the `bmm-*` classes are styled
// (the legacy per-widget `gmm-`/`w1x2m-` classes are unstyled query hooks, so the
// shared component omits them — the A/B pixel-diff confirms parity). Tags the
// host `.placed-module` with `betting-module`.

import { useCallback, useEffect, useLayoutEffect, useRef } from "react";
import type { Ref } from "react";
import { select } from "d3-selection";
import { scaleLinear, scalePoint } from "d3-scale";
import { line, curveMonotoneX } from "d3-shape";
import type { MovementPoint, MovementShape } from "@bento/shared";

function draw(chartEl: HTMLElement, pts: MovementPoint[]): void {
  const w = chartEl.clientWidth;
  const h = chartEl.clientHeight;
  if (w < 20 || h < 20) return; // not laid out yet — the ResizeObserver retries

  const ML = 44,
    MR = 16,
    MT = 8,
    MB = 22;
  const cW = w - ML - MR;
  const cH = h - MT - MB;

  const os = pts.map((p) => p.o);
  const minO = Math.min(...os);
  const maxO = Math.max(...os);
  const pad = (maxO - minO) * 0.18 || 0.05;

  const xSc = scalePoint()
    .domain(pts.map((p) => p.t))
    .range([0, cW])
    .padding(0.2);
  const ySc = scaleLinear()
    .domain([minO - pad, maxO + pad])
    .range([cH, 0]);

  select(chartEl).select("svg").remove();
  const svg = select(chartEl)
    .append("svg")
    .attr("width", w)
    .attr("height", h);
  const g = svg.append("g").attr("transform", `translate(${ML},${MT})`);

  const ticks = ySc.ticks(4);
  // Horizontal grid lines + y-axis labels.
  ticks.forEach((tk) => {
    g.append("line")
      .attr("x1", 0)
      .attr("x2", cW)
      .attr("y1", ySc(tk))
      .attr("y2", ySc(tk))
      .attr("stroke", "rgba(255,255,255,0.06)")
      .attr("stroke-width", 1);
  });
  ticks.forEach((tk) => {
    g.append("text")
      .attr("x", -6)
      .attr("y", ySc(tk))
      .attr("dy", "0.32em")
      .attr("text-anchor", "end")
      .attr("fill", "#999")
      .style("font-size", "var(--t-label)")
      .text(tk.toFixed(2));
  });

  // X-axis labels — thinned to the available width.
  const minLabelGap = 28;
  const maxLabels = Math.max(2, Math.floor(cW / minLabelGap));
  const step = Math.ceil(pts.length / maxLabels);
  pts.forEach((d, i) => {
    if (i % step !== 0 && i !== pts.length - 1) return;
    g.append("text")
      .attr("x", xSc(d.t)!)
      .attr("y", cH + 14)
      .attr("text-anchor", "middle")
      .attr("fill", "#999")
      .style("font-size", "var(--t-label)")
      .text(d.t);
  });

  // Line.
  const lineFn = line<MovementPoint>()
    .x((d) => xSc(d.t)!)
    .y((d) => ySc(d.o))
    .curve(curveMonotoneX);
  g.append("path")
    .attr("fill", "none")
    .attr("stroke", "#6490d8")
    .attr("stroke-width", 2)
    .attr("d", lineFn(pts) ?? "");

  // Dots — the last point (current) is white on a blue ring, the rest solid blue.
  pts.forEach((d, i) => {
    const last = i === pts.length - 1;
    g.append("circle")
      .attr("class", "chart-dot")
      .attr("cx", xSc(d.t)!)
      .attr("cy", ySc(d.o))
      .attr("r", 3.5)
      .attr("fill", last ? "#fff" : "#6490d8")
      .attr("stroke", last ? "#6490d8" : "none")
      .attr("stroke-width", 1.5);
  });
}

export interface MovementChartProps {
  /** Wrapper class, e.g. "gmm-wrapper" / "cmm-wrapper" / "w1x2m-wrapper". */
  wrapperClass: string;
  shape: MovementShape;
  /** Optional ref to the wrapper div. The Player movements use it to find the
   *  shell `.player-toggle` they populate (the Phase-4-adjacent seam); the other
   *  consumers omit it. */
  rootRef?: Ref<HTMLDivElement>;
}

export default function MovementChart({
  wrapperClass,
  shape,
  rootRef,
}: MovementChartProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<HTMLDivElement>(null);
  const { pts, label, openStr, nowStr, moveStr, arrowColour, moveColour } =
    shape;

  // Set the internal ref (for the betting-module tag + layout) and forward the
  // same node to `rootRef` when a caller passed one.
  const setWrapper = useCallback(
    (el: HTMLDivElement | null) => {
      wrapperRef.current = el;
      if (typeof rootRef === "function") rootRef(el);
      else if (rootRef) rootRef.current = el;
    },
    [rootRef]
  );

  useLayoutEffect(() => {
    const mod = wrapperRef.current?.closest<HTMLElement>(".placed-module");
    mod?.classList.add("betting-module");
    return () => mod?.classList.remove("betting-module");
  }, []);

  const sig = JSON.stringify(pts);
  useEffect(() => {
    const chartEl = chartRef.current;
    if (!chartEl) return;
    draw(chartEl, pts);
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => draw(chartEl, pts));
    ro.observe(chartEl);
    return () => ro.disconnect();
    // `sig` captures the plotted series (the complete draw input); `pts` fresh.
  }, [sig]);

  return (
    <div className={`${wrapperClass} bm-wrapper`} ref={setWrapper}>
      <div className="bmm-stats">
        <div>
          <div className="bmm-stat-lbl">OPEN</div>
          <div className="bmm-open-val">{openStr}</div>
        </div>
        <div className="bmm-arrow" style={{ color: arrowColour }}>
          →
        </div>
        <div>
          <div className="bmm-stat-lbl">NOW</div>
          <div className="bmm-now-val">{nowStr}</div>
        </div>
        <div className="bmm-move">
          <div className="bmm-stat-lbl">MOVE</div>
          <div className="bmm-move-val" style={{ color: moveColour }}>
            {moveStr}
          </div>
        </div>
      </div>
      <div className="bmm-label">
        <div className="bmm-label-txt">{label}</div>
      </div>
      <div className="bmm-chart" ref={chartRef} />
    </div>
  );
}
